import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL } from '@utils/api/endpoints.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { CUSTOM_MODEL_QUERY_KEYS } from './query-keys';

export interface Models {
  count: number;
  total_results: number;
  result: Model;
}

export interface Model {
  id: number;
  key: string;
  name: string;
  last_modified: string;
  organization_id: number;
  language_data: null;
  is_trainable: boolean;
  type: number;
  base_language_id: number;
  base_language_key: string;
  training_log: {
    language_id: number;
    training_status: number;
    log_name: null;
  };
}

export const useGetCustomModel = (id: number | undefined) => {
  const { t } = useTranslation();

  const fetchModel = async () => {
    if (id === undefined) {
      throw new Error('Model id is required');
    }
    const response = await Axios.get<Models>(
      GET_URL(id.toString()).customModelDetail
    );
    return response.data;
  };

  return useQuery<Models, AxiosError<BaseError>>(
    CUSTOM_MODEL_QUERY_KEYS.detail(id!),
    fetchModel,
    {
      enabled: Boolean(id),
      // Only poll while training is actually happening (READY_TO_RUN=2 or
      // RUNNING=3). For idle/success/failed models nothing changes on its own
      // and the periodic refetch was making the whole detail page re-render
      // every 20s.
      refetchInterval: data => {
        const s = data?.result?.training_log?.training_status;
        return s === 2 || s === 3 ? 20000 : false;
      },
      refetchIntervalInBackground: true,
      // See note on use-get-custom-models.tsx: focus-based refetch made the
      // detail page flicker on stray focus events. Polling above covers the
      // active-training case; mutations cover user-driven changes.
      refetchOnWindowFocus: false,
      staleTime: 10000, // 10 seconds
      onError: (error: AxiosError<BaseError>) => {
        const errorMessage =
          error.response?.data?.error_description ||
          error.response?.data?.detail ||
          error.response?.data?.error ||
          error.response?.data?.message ||
          t('an-error-occurred-while-fetching-the-custom-model');
        toast.error(errorMessage);
      }
    }
  );
};
